import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { get } from 'lodash';
import styled from 'styled-components';
import { LayoutContext, AuthContext } from 'contexts';
import { capitalize } from 'utils';
import {
  Page,
  RadioGroup,
  RadioButton,
  Button,
  Select,
  InputTextArea,
  UserSelect,
} from 'components';
import { Color, Button as ButtonV2 } from '@bb/elements-web';
import NeedContext from '../NeedContext';

const EditNeed: React.FC = () => {
  const { needId } = useParams<Record<string, string>>();
  const history = useHistory();
  const { setAppTitle, scrollContainer } = React.useContext(LayoutContext);
  const { need, emptyNeed, saveNeed, deleteNeed, isAuthorizedToEdit } =
    React.useContext(NeedContext);
  const { auth } = React.useContext(AuthContext);
  const [invalid, setInvalid] = React.useState<string[]>([]);

  const [currentNeed, setCurrentNeed] = React.useState<typeof need>(emptyNeed);

  const title = React.useMemo(() => {
    if (!currentNeed) return '';

    if (currentNeed.id) {
      return `Edit ${capitalize(currentNeed.type)}`;
    }
    const type = currentNeed.type ? capitalize(currentNeed.type) : 'Have/Want';
    return `New ${type}`;
  }, [currentNeed]);

  const validateFields = () => {
    const requiredFields = ['subject', 'note', 'rep.id', 'type'];
    const invalidFields = requiredFields.filter(
      path => !get(currentNeed, path),
    );
    setInvalid(invalidFields);

    if (!invalidFields.length) {
      saveNeed(currentNeed);
    }
  };

  const handleChange = <T extends keyof typeof currentNeed>(
    key: T,
    value: (typeof currentNeed)[T],
  ) => {
    setCurrentNeed(prev => ({ ...prev, [key]: value }));
    const invalidFields = invalid.filter(field => !field.includes(key));
    setInvalid(invalidFields);
  };

  const getError = (key: string) => {
    const hasError = invalid.some(field => field === key);
    if (hasError) {
      return 'This is a required field.';
    }
    return false;
  };

  React.useEffect(() => {
    setAppTitle(title);
  }, [title]);

  React.useEffect(() => {
    if (isAuthorizedToEdit) {
      setCurrentNeed({
        ...(needId ? need : emptyNeed),
        ...(!auth.isAdmin && {
          rep: auth,
        }),
      });
    }
  }, [isAuthorizedToEdit]);

  if (!isAuthorizedToEdit) {
    history.replace(`/needs/${needId}/view`);
    return null;
  }

  return (
    <Page type="filled" title={title} showBackButton>
      <StyledGrid>
        {!need.id && (
          <RadioGroup
            label="Type"
            buttonStyle="solid"
            value={currentNeed.type}
            onChange={({ target: { value } }) =>
              setCurrentNeed(prev => ({ ...prev, type: value, subject: null }))
            }
          >
            <StyledRadioButton value="have">Have</StyledRadioButton>
            <StyledRadioButton value="want">Want</StyledRadioButton>
          </RadioGroup>
        )}
        {currentNeed.type && (
          <>
            <Select
              label="Subject"
              value={currentNeed.subject}
              onChange={subject => handleChange('subject', subject)}
              error={getError('subject')}
              options={
                currentNeed.type === 'want'
                  ? [
                      { label: 'Property', value: 'Property' },
                      { label: 'Lease', value: 'Lease' },
                      { label: 'Short Term Lease', value: 'Short Term Lease' },
                      { label: 'Service Provider', value: 'Service Provider' },
                      { label: 'Open House Help', value: 'Open House Help' },
                      { label: 'Miscellaneous', value: 'Miscellaneous' },
                    ]
                  : [
                      {
                        label: 'Interested Seller - Sale',
                        value: 'Interested Seller - Sale',
                      },
                      {
                        label: 'Interested Seller - Lease',
                        value: 'Interested Seller - Lease',
                      },
                      { label: 'Open House Help', value: 'Open House Help' },
                      { label: 'Miscellaneous', value: 'Miscellaneous' },
                    ]
              }
            />
            <InputTextArea
              label="Note"
              autoSize={{
                minRows: 4,
                maxRows: 10,
              }}
              value={currentNeed.note}
              onChange={({ target: { value } }) => handleChange('note', value)}
              error={getError('note')}
            />
            {auth.isAdmin && (
              <UserSelect
                label="Agent"
                value={[currentNeed.rep.id]}
                onChange={(ids, reps) => handleChange('rep', reps?.[0])}
                error={getError('rep.id')}
                getPopupContainer={() => scrollContainer?.parentElement}
                showSearch
              />
            )}
            <StyledButtonWrapper>
              {currentNeed.id && (
                <Button danger onClick={deleteNeed}>
                  Delete
                </Button>
              )}
              <ButtonV2
                onClick={validateFields}
                dataCy="save-needs-button"
                size="small"
              >
                Save
              </ButtonV2>
            </StyledButtonWrapper>
          </>
        )}
      </StyledGrid>
    </Page>
  );
};

const StyledGrid = styled.div`
  width: 100%;
  max-width: 400px;
  padding: 0 25px;
  > * {
    margin-bottom: 25px;
  }
`;

const StyledButtonWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 10px;
`;

const StyledRadioButton = styled(RadioButton)`
  .ant-radio-button-checked {
    background: ${Color.PRIMARY};
    border-color: ${Color.PRIMARY};
  }
`;

export default EditNeed;
